/**
 * Configuração declarativa dos fluxos de importação de documentos obrigatórios.
 *
 * Cada fluxo define:
 * - titulo: título exibido no modal
 * - getArquivosBase(dados): retorna a lista de nomes de arquivo esperados
 *   para aquela OS (pode variar conforme dados.tipo?.id)
 * - regrasRemocao: lista de { arquivos, situacaoPermitida } — um arquivo só
 *   pode ser removido quando dados.situacao?.id === situacaoPermitida
 *
 * Antes esta lógica estava duplicada em 3 componentes quase idênticos
 * (ImportAberturaAlteracaoEmpresa, ImportAlteracaoEnquadramento,
 * ImportBaixaEncerramentoEmpresa). Agora é só configuração: para adicionar
 * ou ajustar um fluxo, basta editar este arquivo — não é preciso duplicar
 * um componente inteiro.
 */

export const FLUXO_ABERTURA_ALTERACAO_EMPRESA = "ABERTURA_ALTERACAO_EMPRESA";
export const FLUXO_ALTERACAO_ENQUADRAMENTO = "ALTERACAO_ENQUADRAMENTO";
export const FLUXO_BAIXA_ENCERRAMENTO_EMPRESA = "BAIXA_ENCERRAMENTO_EMPRESA";

export const FLUXOS_DOCUMENTOS = {
  [FLUXO_ABERTURA_ALTERACAO_EMPRESA]: {
    titulo: "Importar Documentos Abertura/Alteração de Empresa",
    getArquivosBase: (dados) => {
      const base = ["Viabilidade-prefeitura", "FCPJ-RedeSim", "Procuracao-ECAC"];

      if (dados.tipo?.id === "4") {
        // inscricao-municipal saiu daqui: pra Alteração, agora é documento
        // avulso (docsContexto + upload simples), ver verificarDocumentosCCM.
        base.push("Inscricao-estadual");
      } else {
        base.push("Termo-de-regime-tributario", "Inscricao-estadual", "inscricao-municipal");
      }

      return base;
    },
    regrasRemocao: [
      { arquivos: ["Viabilidade-prefeitura"], situacaoPermitida: "V-CCM" },
      { arquivos: ["FCPJ-RedeSim"], situacaoPermitida: "A-RFB" },
      { arquivos: ["Procuracao-ECAC", "Termo-de-regime-tributario", "Inscricao-estadual"], situacaoPermitida: "A-JCM" },
      { arquivos: ["inscricao-municipal"], situacaoPermitida: "A-CCM" },
    ],
  },

  [FLUXO_ALTERACAO_ENQUADRAMENTO]: {
    titulo: "Importar Documentos Alteração de Enquadramento (Migração) de Empresa",
    getArquivosBase: (dados) => {
      const base = ["Procuracao-ECAC", "Desenquadramento-mei", "Viabilidade-prefeitura", "FCPJ-RedeSim"];

      if (dados.tipo?.id === "3") {
        base.push("Inscricao-estadual");
      }

      return base;
    },
    regrasRemocao: [
      { arquivos: ["Procuracao-ECAC"], situacaoPermitida: "P-DOC" },
      { arquivos: ["Viabilidade-prefeitura"], situacaoPermitida: "V-CCM" },
      { arquivos: ["FCPJ-RedeSim"], situacaoPermitida: "A-RFB" },
      { arquivos: ["Desenquadramento-mei"], situacaoPermitida: "M-TE" },
      { arquivos: ["Procuracao-ECAC", "Inscricao-estadual"], situacaoPermitida: "A-JCM" },
    ],
  },

  [FLUXO_BAIXA_ENCERRAMENTO_EMPRESA]: {
    titulo: "Importar Documentos Baixa/Encerramento de Empresa",
    getArquivosBase: (dados) => {
      const base = [
       /** "CND-Federal",
        "CND-Estadual",
        "CND-Municipal", */
        "FCN",
        "CNPJ",
        "Inscricao-estadual",
        "inscricao-municipal",
        "guia-tfe",
      ];

      if (dados.tipo?.id === "3") {
        base.push("Inscricao-estadual", "inscricao-municipal", "guia-tfe");
      }

      return base;
    },
    regrasRemocao: [
      //{ arquivos: ["CND-Federal", "CND-Estadual", "CND-Municipal"], situacaoPermitida: "V-CND" },
      { arquivos: ["inscricao-municipal", "guia-tfe"], situacaoPermitida: "A-CCM" },
    ],
  },
};

/** Um arquivo sem regra cadastrada é sempre removível (comportamento original). */
export function podeRemoverArquivo(nomeEtapa, situacaoId, regrasRemocao) {
  const regra = regrasRemocao.find((r) => r.arquivos.includes(nomeEtapa));
  if (!regra) return true;
  return situacaoId === regra.situacaoPermitida;
}

function normalizarNomeArquivo(caminho) {
  const nomeComExtensao = caminho.split(/[\\/]/).pop().trim();
  return nomeComExtensao.split(".").slice(0, -1).join(".").toLowerCase();
}

/**
 * Diz se ainda falta algum arquivo obrigatório do fluxo informado.
 * Usado para decidir se a trilha de progresso deve destacar a etapa atual
 * em amarelo (documento pendente) ou azul (nada pendente / já identificado).
 *
 * Segue a mesma regra de isenção da Inscrição Estadual usada no modal de
 * importação: se a empresa não tem IE cadastrada, essa etapa não conta
 * como pendente.
 */
export function temArquivosPendentes(fluxo, dados, objDocs) {
  const config = FLUXOS_DOCUMENTOS[fluxo];
  if (!config || !dados) return false;

  const esperados = config.getArquivosBase(dados);
  const carregados = (objDocs || []).map(normalizarNomeArquivo);

  return esperados.some((nome) => {
    const nomeNormalizado = nome.toLowerCase();
    if (carregados.includes(nomeNormalizado)) return false;
    if (nomeNormalizado === "inscricao-estadual" && !dados?.empresa?.ie) return false;
    return true;
  });
}
